/**
 * Comparing two versions of a skill (ADR-216 Amendment 1): which files were
 * added, removed and changed, and each changed file's lines, unified or side
 * by side. The diff runs here from the two versions' files; jsdiff loads only
 * when a comparison is on screen.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { skillsApi, type SkillDetail, type SkillVersionFiles } from '@/services/skillsApi';
import { cn } from '@/lib/utils';
import { compareFiles, diffRows, foldRows, type DiffRow, type FileChange, type SkillFileText } from './skillFiles';

type LineRow = Exclude<DiffRow, { kind: 'fold' }>;

type DiffLines = (a: string, b: string) => { value: string; added?: boolean; removed?: boolean }[];

/** jsdiff's diffLines, loaded on first use. */
function useDiffLines(): DiffLines | null {
  const [fn, setFn] = useState<DiffLines | null>(null);
  useEffect(() => {
    let live = true;
    import('diff').then((m) => live && setFn(() => m.diffLines as DiffLines));
    return () => {
      live = false;
    };
  }, []);
  return fn;
}

const STATUS_STYLE: Record<FileChange['status'], string> = {
  added: 'text-emerald-700 dark:text-emerald-400',
  removed: 'text-red-700 dark:text-red-400',
  changed: 'text-amber-700 dark:text-amber-400',
  unchanged: 'text-slate-400',
};

const ROW_STYLE = {
  add: 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
  del: 'bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-200',
  same: 'text-slate-700 dark:text-slate-300',
} as const;

const MARK = { add: '+', del: '-', same: ' ' } as const;

function Fold({ count, span }: { count: number; span: number }) {
  return (
    <tr>
      <td colSpan={span} className="bg-slate-50 px-3 py-0.5 text-slate-400 dark:bg-slate-900">
        ⋯ {count} unchanged line{count === 1 ? '' : 's'}
      </td>
    </tr>
  );
}

function Unified({ rows }: { rows: DiffRow[] }) {
  return (
    <table className="w-full border-collapse font-mono text-xs leading-5" data-testid="skill-diff-unified">
      <tbody>
        {rows.map((r, i) =>
          r.kind === 'fold' ? (
            <Fold key={i} count={r.count} span={4} />
          ) : (
            <tr key={i} className={ROW_STYLE[r.kind]} data-kind={r.kind}>
              <td className="w-10 select-none px-2 text-right text-slate-400">{r.oldNo ?? ''}</td>
              <td className="w-10 select-none px-2 text-right text-slate-400">{r.newNo ?? ''}</td>
              <td className="w-4 select-none">{MARK[r.kind]}</td>
              <td className="whitespace-pre-wrap break-all pr-3">{r.text}</td>
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}

/** Side by side: a removed run faces the added run that replaced it. */
function SideBySide({ rows }: { rows: DiffRow[] }) {
  type Cell = { no?: number; text: string; kind: 'add' | 'del' | 'same' } | null;
  const pairs: ([Cell, Cell] | { fold: number })[] = [];
  for (let i = 0; i < rows.length; ) {
    const r = rows[i];
    if (r.kind === 'fold') {
      pairs.push({ fold: r.count });
      i++;
    } else if (r.kind === 'same') {
      pairs.push([{ no: r.oldNo, text: r.text, kind: 'same' }, { no: r.newNo, text: r.text, kind: 'same' }]);
      i++;
    } else {
      const dels: Cell[] = [];
      const adds: Cell[] = [];
      while (i < rows.length && rows[i].kind === 'del') {
        const d = rows[i++] as LineRow;
        dels.push({ no: d.oldNo, text: d.text, kind: 'del' });
      }
      while (i < rows.length && rows[i].kind === 'add') {
        const a = rows[i++] as LineRow;
        adds.push({ no: a.newNo, text: a.text, kind: 'add' });
      }
      for (let k = 0; k < Math.max(dels.length, adds.length); k++) pairs.push([dels[k] ?? null, adds[k] ?? null]);
    }
  }
  const cell = (c: Cell, key: string) => [
    <td key={`${key}n`} className="w-10 select-none px-2 text-right text-slate-400">
      {c?.no ?? ''}
    </td>,
    <td key={`${key}t`} className={cn('w-1/2 whitespace-pre-wrap break-all pr-3', c && ROW_STYLE[c.kind])}>
      {c?.text ?? ''}
    </td>,
  ];
  return (
    <table className="w-full table-fixed border-collapse font-mono text-xs leading-5" data-testid="skill-diff-split">
      <tbody>
        {pairs.map((p, i) =>
          'fold' in p ? (
            <Fold key={i} count={p.fold} span={4} />
          ) : (
            <tr key={i}>
              {cell(p[0], 'l')}
              {cell(p[1], 'r')}
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}

function FileDiff({
  change,
  before,
  after,
  diffLines,
  split,
}: {
  change: FileChange;
  before: string;
  after: string;
  diffLines: DiffLines | null;
  split: boolean;
}) {
  const [open, setOpen] = useState(change.status !== 'unchanged');
  const rows = useMemo(
    () => (diffLines && open ? foldRows(diffRows(diffLines(before, after))) : []),
    [diffLines, open, before, after],
  );
  const added = rows.filter((r) => r.kind === 'add').length;
  const removed = rows.filter((r) => r.kind === 'del').length;
  return (
    <li className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800" data-testid="skill-diff-file" data-path={change.path}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 bg-slate-50 px-3 py-2 text-left text-xs dark:bg-slate-900"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <span className="font-mono text-slate-800 dark:text-slate-200">{change.path}</span>
        <span className={cn('font-medium', STATUS_STYLE[change.status])}>{change.status}</span>
        {open && diffLines && (
          <span className="ml-auto text-slate-500">
            <span className="text-emerald-700 dark:text-emerald-400">+{added}</span>{' '}
            <span className="text-red-700 dark:text-red-400">−{removed}</span>
          </span>
        )}
      </button>
      {open && (
        <div className="max-h-[32rem] overflow-auto">
          {!diffLines ? (
            <p className="p-3 text-xs text-slate-400">Comparing…</p>
          ) : split ? (
            <SideBySide rows={rows} />
          ) : (
            <Unified rows={rows} />
          )}
        </div>
      )}
    </li>
  );
}

/** Everything that changed from `from` to `to`, file by file. */
export function SkillDiff({
  from,
  to,
  showUnchanged = false,
}: {
  from: readonly SkillFileText[];
  to: readonly SkillFileText[];
  showUnchanged?: boolean;
}) {
  const diffLines = useDiffLines();
  const [split, setSplit] = useState(false);
  const changes = useMemo(() => compareFiles(from, to), [from, to]);
  const before = useMemo(() => new Map(from.map((f) => [f.path, f.content])), [from]);
  const after = useMemo(() => new Map(to.map((f) => [f.path, f.content])), [to]);
  const count = (s: FileChange['status']) => changes.filter((c) => c.status === s).length;
  const shown = showUnchanged ? changes : changes.filter((c) => c.status !== 'unchanged');
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs" data-testid="skill-diff-summary">
        <span className={STATUS_STYLE.changed}>{count('changed')} changed</span>
        <span className={STATUS_STYLE.added}>{count('added')} added</span>
        <span className={STATUS_STYLE.removed}>{count('removed')} removed</span>
        <span className="text-slate-500">{count('unchanged')} unchanged</span>
        <div className="ml-auto flex rounded-md border border-slate-200 dark:border-slate-700" role="group">
          {(['Unified', 'Side by side'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={(mode === 'Side by side') === split}
              onClick={() => setSplit(mode === 'Side by side')}
              data-testid={mode === 'Unified' ? 'skill-diff-unified-toggle' : 'skill-diff-split-toggle'}
              className={cn(
                'px-2 py-1',
                (mode === 'Side by side') === split
                  ? 'bg-slate-100 font-medium text-slate-900 dark:bg-slate-800 dark:text-slate-100'
                  : 'text-slate-500',
              )}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="skill-diff-none">
          No file changed.
        </p>
      ) : (
        <ul className="space-y-3">
          {shown.map((c) => (
            <FileDiff
              key={c.path}
              change={c}
              before={before.get(c.path) ?? ''}
              after={after.get(c.path) ?? ''}
              diffLines={diffLines}
              split={split}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** /docs/skills/:name/compare?from=a&to=b — any two kept versions. */
export function SkillComparePage() {
  const { name = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [skill, setSkill] = useState<SkillDetail | null>(null);
  const [sides, setSides] = useState<[SkillVersionFiles, SkillVersionFiles] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    skillsApi
      .get(name)
      .then(setSkill)
      .catch(() => setError('This skill does not exist, or you cannot read its space.'));
  }, [name]);

  const kept = skill?.versions.map((v) => v.version) ?? [];
  const to = Number(params.get('to')) || kept[0];
  const from = Number(params.get('from')) || kept.find((v) => v < to) || to;

  useEffect(() => {
    if (!skill || !from || !to) return;
    setSides(null);
    Promise.all([skillsApi.version(name, from), skillsApi.version(name, to)])
      .then((both) => setSides(both))
      .catch(() => setError('One of these versions is no longer kept (the newest 20 are).'));
  }, [skill, name, from, to]);

  function choose(side: 'from' | 'to', version: number) {
    const next = { from, to, [side]: version };
    navigate(`/docs/skills/${encodeURIComponent(name)}/compare?from=${next.from}&to=${next.to}`);
  }

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!skill) return <LoadingState />;

  const picker = (side: 'from' | 'to', value: number) => (
    <select
      value={value}
      onChange={(e) => choose(side, Number(e.target.value))}
      data-testid={`skill-compare-${side}`}
      className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
    >
      {skill.versions.map((v) => (
        <option key={v.version} value={v.version}>
          version {v.version}
        </option>
      ))}
    </select>
  );

  return (
    <div className="mx-auto max-w-5xl" data-testid="skill-compare">
      <Link
        to={`/docs/skills/${encodeURIComponent(name)}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" /> {name}
      </Link>
      <h2 className="mb-3 font-mono text-lg font-semibold text-slate-900 dark:text-slate-100">Compare versions</h2>
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
        {picker('from', from)} <span>→</span> {picker('to', to)}
      </div>
      {skill.versions
        .filter((v) => v.version > Math.min(from, to) && v.version <= Math.max(from, to) && v.note)
        .map((v) => (
          <p key={v.version} className="mb-1 text-xs text-slate-500" data-testid="skill-compare-note">
            version {v.version}: {v.note}
          </p>
        ))}
      <div className="mt-4">
        {sides ? <SkillDiff from={sides[0].files} to={sides[1].files} /> : <LoadingState />}
      </div>
    </div>
  );
}
